/**
 * Benefícios — catálogo geral de TIPOS (pedido do dono: criar novos e editar os
 * existentes). Linha enxuta (pedido do dono 23/09/2026): só nome + descrição +
 * botões de ação visíveis (Editar/Histórico/Tarifas-vt/Desativar — sem badges e
 * sem menu "..."; o badge "desativado atualmente" fica só na visão de data-base).
 * Pedido 18/08/2026: ação "Histórico" por tipo (linha do tempo das modificações
 * + vigências do parâmetro global do papel, com "Abrir configuração") e, no
 * modal de edição, mudança de VALORES do desconto pede a data-base e mostra a
 * prévia da propagação ("ponto de histórico em N funcionários a partir de
 * MM/AAAA") — o PATCH propaga congelando o valor atual (regra de ouro dos
 * vínculos). Mudança documental segue in place, sem histórico de vínculos.
 */
import { useEffect, useState } from "react";
import { ActionIcon, Autocomplete, Badge, Button, Card, Group, Modal, Select, Text, TextInput, Timeline, Title, Tooltip, Switch } from "@mantine/core";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import { api, ErroApi } from "../api";
import { CompetenciaField, MoedaField, PercentField } from "../componentes/entradas-mantine";
import { AvisosAlert, Confirmacao, avisosDe, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { ErroApiAlert } from "../componentes/notificacoes";
import { fmtCompetencia, fmtDataHora, fmtMoeda } from "../format";
import { montarCadeiasTarifas, type CadeiaTarifa, type GrupoTarifaSolta, type TarifaCatalogo, type UsoTarifa } from "../beneficios-tarifas";

interface Tipo {
  id: string;
  nome: string;
  descricao: string | null;
  natureza: string;
  modeloCalculo: string;
  papel: string;
  incideInss: boolean | null;
  incideIrrf: boolean | null;
  incideFgts: boolean | null;
  eventoId: string | null;
  modeloDesconto?: string | null;
  descontoValorCentavos?: number | null;
  descontoPercentualPpm?: number | null;
  descontoTetoCentavos?: number | null;
  ativo: boolean;
  vinculosAtivos: number;
  /** Presentes só na listagem com data-base (19/08/2026): a versão vigente nela
   *  e se o tipo está desativado HOJE (a data-base o mostra porque ele existia). */
  versao?: VersaoTipoUi;
  desativadoAtualmente?: boolean;
}
interface EventoOpcao {
  id: string;
  codigoDominio: number | null;
  descricao: string;
}

export const ROTULO_NATUREZA: Record<string, string> = {
  provento: "provento",
  desconto: "desconto",
  informativo: "informativo",
};
export const ROTULO_MODELO: Record<string, string> = {
  valor_fixo_mensal: "valor fixo mensal",
  valor_diario: "valor por dia",
  cap_salario_custo: "cap % salário × custo (VT)",
  plano_saude_rateio: "plano com rateio por beneficiário",
  custom_informativo: "custom (só registro — não entra no cálculo)",
};
/** Tarefa A — rótulos do modelo de DESCONTO (beneficios@v3_2026). */
export const ROTULO_MODELO_DESCONTO: Record<string, string> = {
  nenhum: "nenhum (sem desconto do funcionário)",
  valor_fixo: "valor fixo em R$",
  percentual_salario: "% do salário do mês",
  percentual_salario_teto: "% do salário com teto em R$",
  cap_salario_custo: "cap: % do salário limitado ao custo (VT)",
  limite_valor: "valor limitado a um teto em R$",
};
function competenciaAtual(): number {
  const hoje = new Date();
  return hoje.getUTCFullYear() * 100 + (hoje.getUTCMonth() + 1);
}

/** Os campos de desconto normalizados como o PATCH espera (null fora do modelo). */
function camposDesconto(modeloDesconto: string, descValor: number | null, descPct: number | null, descTeto: number | null) {
  return {
    modeloDesconto: modeloDesconto === "" ? null : modeloDesconto,
    descontoValorCentavos: modeloDesconto === "valor_fixo" || modeloDesconto === "limite_valor" ? descValor : null,
    descontoPercentualPpm:
      modeloDesconto === "percentual_salario" || modeloDesconto === "percentual_salario_teto" || modeloDesconto === "cap_salario_custo"
        ? descPct
        : null,
    descontoTetoCentavos: modeloDesconto === "percentual_salario_teto" || modeloDesconto === "limite_valor" ? descTeto : null,
  };
}

function ModalTipo({
  tipo,
  eventos,
  onFechar,
  onSalvo,
}: {
  /** null = criação de tipo novo. */
  tipo: Tipo | null;
  eventos: EventoOpcao[];
  onFechar: () => void;
  onSalvo: (avisos: string[]) => void;
}) {
  const criacao = tipo === null;
  const [nome, setNome] = useState(tipo?.nome ?? "");
  const [descricao, setDescricao] = useState(tipo?.descricao ?? "");
  const [natureza, setNatureza] = useState(tipo?.natureza ?? "informativo");
  const [modelo, setModelo] = useState(tipo?.modeloCalculo ?? "custom_informativo");
  const [papel, setPapel] = useState(tipo?.papel ?? "custom");
  const [inss, setInss] = useState(tipo?.incideInss ?? false);
  const [irrf, setIrrf] = useState(tipo?.incideIrrf ?? false);
  const [fgts, setFgts] = useState(tipo?.incideFgts ?? false);
  const [eventoId, setEventoId] = useState<string | null>(tipo?.eventoId ?? null);
  // Tarefa A — regra de desconto do tipo ("" = derivado do papel, como sempre foi)
  const [modeloDesconto, setModeloDesconto] = useState(tipo?.modeloDesconto ?? "");
  const [descValor, setDescValor] = useState<number | null>(tipo?.descontoValorCentavos ?? null);
  const [descPct, setDescPct] = useState<number | null>(tipo?.descontoPercentualPpm ?? null);
  const [descTeto, setDescTeto] = useState<number | null>(tipo?.descontoTetoCentavos ?? null);
  const [erro, setErro] = useState<string | null>(null);
  // Propagação do valor padrão (pedido 18/08/2026): data-base da mudança + prévia
  const [mudanca, setMudanca] = useState<number | null>(competenciaAtual());
  const [previaN, setPreviaN] = useState<number | null>(null);

  const desconto = camposDesconto(modeloDesconto, descValor, descPct, descTeto);
  const mudaValores =
    !criacao &&
    (desconto.modeloDesconto !== (tipo!.modeloDesconto ?? null) ||
      desconto.descontoValorCentavos !== (tipo!.descontoValorCentavos ?? null) ||
      desconto.descontoPercentualPpm !== (tipo!.descontoPercentualPpm ?? null) ||
      desconto.descontoTetoCentavos !== (tipo!.descontoTetoCentavos ?? null));

  // prévia: quantos vínculos ganham ponto de histórico na data-base
  useEffect(() => {
    if (!mudaValores || mudanca === null) {
      setPreviaN(null);
      return;
    }
    let vivo = true;
    api
      .get<{ total: number }>(`/beneficio-tipos/${tipo!.id}/vinculos-vigentes?competencia=${mudanca}`)
      .then((r) => vivo && setPreviaN(r.total))
      .catch(() => vivo && setPreviaN(null));
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mudaValores, mudanca, tipo?.id]);

  async function salvar() {
    setErro(null);
    try {
      const corpo = {
        nome: nome.trim(),
        descricao: descricao || null,
        natureza,
        modeloCalculo: modelo,
        papel,
        incideInss: inss,
        incideIrrf: irrf,
        incideFgts: fgts,
        eventoId,
        ...desconto,
      };
      if (criacao) {
        await api.post("/beneficio-tipos", corpo);
        notificarOk("Tipo de benefício criado.");
        onSalvo([]);
      } else {
        const resposta = await api.patch(`/beneficio-tipos/${tipo!.id}`, {
          ...corpo,
          ...(mudaValores ? { vigenciaMudanca: mudanca } : {}),
        });
        const prop = (resposta as { propagacao?: { vigenciaMudanca: number; vinculosAfetados: number; congelados: number } }).propagacao;
        notificarOk(
          prop
            ? prop.vinculosAfetados > 0
              ? `Valor padrão atualizado — ponto de histórico criado em ${prop.vinculosAfetados} funcionário(s) a partir de ${fmtCompetencia(prop.vigenciaMudanca)}${prop.congelados > 0 ? ` (${prop.congelados} com o valor antigo congelado)` : ""}.`
              : "Tipo atualizado — nenhum vínculo vigente na data-base; só o tipo mudou."
            : "Tipo atualizado.",
        );
        onSalvo(avisosDe(resposta));
      }
      onFechar();
    } catch (e) {
      setErro(e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));
    }
  }

  return (
    <Modal opened onClose={onFechar} title={criacao ? "Novo tipo de benefício" : `Editar — ${tipo!.nome}`} size="lg" centered>
      <Text size="xs" c="dimmed" mb="sm">
        O <strong>papel</strong> é o slot de cálculo no motor atual (vt/vr/caju/HO/seguro/plano — um por tipo). Tipo novo de
        verdade entra como <strong>custom</strong>: fica no histórico e nos relatórios, mas não entra no cálculo (isso exige motor v2).
      </Text>
      <TextInput label="Nome" value={nome} onChange={(e) => setNome(e.currentTarget.value)} required mb="sm" />
      <TextInput label="Descrição" value={descricao} onChange={(e) => setDescricao(e.currentTarget.value)} mb="sm" />
      <Group grow mb="sm">
        <Select label="Natureza" data={Object.entries(ROTULO_NATUREZA).map(([value, label]) => ({ value, label }))} value={natureza} onChange={(v) => setNatureza(v ?? "informativo")} />
        <Select
          label="Modelo de cálculo"
          description="Como o motor calcula este benefício."
          data={Object.entries(ROTULO_MODELO).map(([value, label]) => ({ value, label }))}
          value={modelo}
          onChange={(v) => setModelo(v ?? "custom_informativo")}
        />
      </Group>
      <Group grow mb="sm">
        <Select
          label="Papel (slot do motor)"
          data={[
            { value: "custom", label: "custom — informativo (não calcula)" },
            { value: "vt", label: "vt — vale-transporte" },
            { value: "vr", label: "vr — vale-refeição" },
            { value: "caju_mais", label: "caju_mais — Caju+" },
            { value: "auxilio_home_office", label: "auxilio_home_office" },
            { value: "seguro_vida", label: "seguro_vida" },
            { value: "plano_saude", label: "plano_saude" },
          ]}
          value={papel}
          onChange={(v) => setPapel(v ?? "custom")}
        />
        <Select
          label="Evento do catálogo (de-para)"
          description="Rubrica usada em exports/conferência."
          data={eventos.map((e) => ({ value: e.id, label: `${e.codigoDominio ?? "s/c"} ${e.descricao}` }))}
          value={eventoId}
          onChange={setEventoId}
          clearable
          searchable
        />
      </Group>
      <Group mb="sm">
        <Switch label="Incide INSS" checked={inss} onChange={(e) => setInss(e.currentTarget.checked)} />
        <Switch label="Incide IRRF" checked={irrf} onChange={(e) => setIrrf(e.currentTarget.checked)} />
        <Switch label="Incide FGTS" checked={fgts} onChange={(e) => setFgts(e.currentTarget.checked)} />
      </Group>
      <Select
        label="Desconto do funcionário (regra)"
        description="Como o desconto deste benefício é calculado (motor v4 / beneficios@v3_2026). Vazio = o padrão de sempre do papel."
        data={[{ value: "", label: "padrão do papel (como sempre foi)" }, ...Object.entries(ROTULO_MODELO_DESCONTO).map(([value, label]) => ({ value, label }))]}
        value={modeloDesconto}
        onChange={(v) => setModeloDesconto(v ?? "")}
        mb="sm"
      />
      {(modeloDesconto === "valor_fixo" || modeloDesconto === "limite_valor") && (
        <MoedaField
          label="Valor do desconto"
          description="Vazio = o parâmetro simbólico global (hoje R$ 1,00). O vínculo pode ter override por pessoa."
          valor={descValor}
          onChange={setDescValor}
          mb="sm"
        />
      )}
      {(modeloDesconto === "percentual_salario" || modeloDesconto === "percentual_salario_teto" || modeloDesconto === "cap_salario_custo") && (
        <PercentField
          label="Percentual sobre o salário"
          description="Vazio = o parâmetro global do VT (hoje 6%)."
          valor={descPct}
          onChange={setDescPct}
          mb="sm"
        />
      )}
      {(modeloDesconto === "percentual_salario_teto" || modeloDesconto === "limite_valor") && (
        <MoedaField label="Teto do desconto" valor={descTeto} onChange={setDescTeto} mb="sm" />
      )}
      {mudaValores && (
        <Card withBorder mb="sm" padding="sm">
          <Text size="xs" mb="xs">
            <strong>Mudança de valor padrão com histórico:</strong> o valor atual é congelado e cada funcionário com o
            benefício vigente ganha um <strong>ponto de histórico</strong> (a vigência dele fecha no mês anterior e a nova
            já nasce com a regra nova) — os meses já fechados não mudam.
          </Text>
          <CompetenciaField label="Os novos valores valem a partir de" valor={mudanca} onChange={setMudanca} />
          {previaN !== null && mudanca !== null && (
            <Text size="xs" c="dimmed" mt={6} role="status">
              {previaN > 0
                ? `Esta mudança criará ponto de histórico em ${previaN} funcionário(s) a partir de ${fmtCompetencia(mudanca)}.`
                : `Nenhum vínculo vigente com início anterior a ${fmtCompetencia(mudanca)} — só o tipo será atualizado.`}
            </Text>
          )}
        </Card>
      )}
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>
          Voltar
        </Button>
        <Button onClick={() => void salvar()} disabled={!nome.trim() || (mudaValores && mudanca === null)}>
          {criacao ? "Criar tipo" : "Salvar alterações"}
        </Button>
      </Group>
    </Modal>
  );
}

/** Rótulos dos escalares conhecidos do payload dos parâmetros globais. */
const ROTULO_VALOR_PARAM: Record<string, string> = {
  valorCentavos: "valor",
  vrDiarioCentavos: "valor por dia",
  percentualMaximoPpm: "% máximo do salário",
};

function fmtValorParam(chave: string, valor: number): string {
  return chave.endsWith("Ppm") ? `${(valor / 10000).toFixed(2).replace(".", ",")}%` : fmtMoeda(valor);
}

function linhaVigenciaParam(v: { vigenciaInicio: number; vigenciaFim: number | null; valores: Record<string, number> }): string {
  const valores = Object.entries(v.valores)
    .map(([k, x]) => `${ROTULO_VALOR_PARAM[k] ?? k}: ${fmtValorParam(k, x)}`)
    .join(" · ");
  return `${fmtCompetencia(v.vigenciaInicio)} → ${v.vigenciaFim ? fmtCompetencia(v.vigenciaFim) : "em vigor"}: ${valores}`;
}

interface EntradaHistorico {
  timestamp: string;
  acao: string;
  titulo: string;
  detalhes: string[];
}
/** Versão do tipo (snapshot dos valores padrão por data-base — 19/08/2026). */
interface VersaoTipoUi {
  vigenciaInicio: number;
  vigenciaFim: number | null;
  natureza: string;
  modeloCalculo: string;
  papel: string;
  modeloDesconto: string | null;
  descontoValorCentavos: number | null;
  descontoPercentualPpm: number | null;
  descontoTetoCentavos: number | null;
  origem: string;
}
interface HistoricoTipo {
  entradas: EntradaHistorico[];
  versoes: VersaoTipoUi[];
  parametros: { chave: string; rotulo: string; vigencias: { vigenciaInicio: number; vigenciaFim: number | null; valores: Record<string, number> }[] }[];
}

const ROTULO_ORIGEM_VERSAO: Record<string, string> = { implantacao: "implantação", criacao: "criação", edicao: "edição" };

/** Linha legível da versão: período + regra de desconto com os valores da época. */
function linhaVersao(v: VersaoTipoUi): string {
  const regra = v.modeloDesconto ? (ROTULO_MODELO_DESCONTO[v.modeloDesconto] ?? v.modeloDesconto) : "padrão do papel";
  const valores: string[] = [];
  if (v.descontoValorCentavos !== null) valores.push(`valor ${fmtMoeda(v.descontoValorCentavos)}`);
  if (v.descontoPercentualPpm !== null) valores.push(`${(v.descontoPercentualPpm / 10000).toFixed(2).replace(".", ",")}% do salário`);
  if (v.descontoTetoCentavos !== null) valores.push(`teto ${fmtMoeda(v.descontoTetoCentavos)}`);
  return `${regra}${valores.length > 0 ? ` — ${valores.join(", ")}` : ""} · ${ROTULO_MODELO[v.modeloCalculo] ?? v.modeloCalculo} · ${ROTULO_ORIGEM_VERSAO[v.origem] ?? v.origem}`;
}

/** Histórico do tipo (pedido do dono 18/08/2026): linha do tempo das modificações
 *  (auditoria: criação, edições com diff, propagações com a contagem de vínculos)
 *  + vigências do parâmetro global do papel — e daqui abre a configuração. */
function ModalHistoricoTipo({ tipo, onFechar, onAbrirConfig }: { tipo: Tipo; onFechar: () => void; onAbrirConfig: () => void }) {
  const [hist, setHist] = useState<HistoricoTipo | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    let vivo = true;
    api
      .get<HistoricoTipo>(`/beneficio-tipos/${tipo.id}/historico`)
      .then((r) => vivo && setHist(r))
      .catch((e) => vivo && setErro(e));
    return () => {
      vivo = false;
    };
  }, [tipo.id]);
  return (
    <Modal opened onClose={onFechar} title={`Histórico — ${tipo.nome}`} size="lg" centered>
      <ErroApiAlert erro={erro} />
      {!hist ? (
        <Carregando linhas={3} />
      ) : (
        <>
          <Text size="xs" c="dimmed" mb="sm">
            As modificações do tipo na linha do tempo (criação, edições e propagações do valor padrão, com a contagem de
            vínculos) — sem testar mês a mês. Depois as <strong>versões</strong> (os valores padrão de cada período) e os
            valores globais ligados ao papel (temporais).
          </Text>
          <Timeline bulletSize={16} lineWidth={1} mb="md">
            {hist.entradas.map((e, i) => (
              <Timeline.Item
                key={i}
                title={
                  <Text size="sm" fw={600}>
                    {fmtDataHora(e.timestamp)} — {e.titulo}
                  </Text>
                }
              >
                {e.detalhes.map((d, j) => (
                  <Text key={j} size="xs" c="dimmed">
                    {d}
                  </Text>
                ))}
              </Timeline.Item>
            ))}
          </Timeline>
          {hist.versoes.length > 0 && (
            <>
              <Text size="xs" fw={700} mb={4}>
                Valores padrão por data-base (versões)
              </Text>
              {hist.versoes.map((v, i) => (
                <Text key={i} size="xs" ml="sm" c={i === 0 ? undefined : "dimmed"}>
                  · {fmtCompetencia(v.vigenciaInicio)} → {v.vigenciaFim ? fmtCompetencia(v.vigenciaFim) : "em vigor"}: {linhaVersao(v)}
                </Text>
              ))}
              <div style={{ marginBottom: 12 }} />
            </>
          )}
          {hist.parametros.length > 0 && (
            <>
              <Text size="xs" fw={700} mb={4}>
                Valores globais ligados ao papel (temporais)
              </Text>
              {hist.parametros.map((p) => (
                <div key={p.chave}>
                  <Text size="xs" c="dimmed">
                    {p.rotulo}
                  </Text>
                  {p.vigencias.map((v, i) => (
                    <Text key={i} size="xs" ml="sm">
                      · {linhaVigenciaParam(v)}
                    </Text>
                  ))}
                </div>
              ))}
            </>
          )}
        </>
      )}
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onFechar}>
          Fechar
        </Button>
        <Button onClick={onAbrirConfig}>Abrir configuração</Button>
      </Group>
    </Modal>
  );
}

/** Tarifas do tipo VT — visão por CADEIA (23/09/2026, 2ª leva): as tarifas são
 *  agrupadas pelo NOME (a cadeia temporal) e cada cadeia mostra TODAS as
 *  vigências em ordem — as encerradas atenuadas, a vigente em destaque e a
 *  futura como "pré-cadastrado". Ações por cadeia: "Novo valor a partir de…"
 *  (form inline → POST de nova vigência do MESMO nome), "quem usa" (vínculos
 *  que referenciam a cadeia) e encerrar a vigência aberta. O vínculo referencia
 *  a cadeia (tipoId+nome): um valor novo passa a valer para os vínculos ligados
 *  sem mexer em nenhum deles. */
function ModalTarifasTipo({ tipo, onFechar }: { tipo: Tipo; onFechar: () => void }) {
  const [dados, setDados] = useState<{ vigentes: TarifaCatalogo[]; historico: TarifaCatalogo[] } | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [nome, setNome] = useState("");
  const [valor, setValor] = useState<number | null>(null);
  const [inicio, setInicio] = useState<number | null>(competenciaAtual());
  const [encerrando, setEncerrando] = useState<TarifaCatalogo | null>(null);
  // form inline "Novo valor a partir de…" da cadeia (a chave é o nome da cadeia)
  const [novoValorCadeia, setNovoValorCadeia] = useState<string | null>(null);
  const [novoValor, setNovoValor] = useState<number | null>(null);
  const [novoInicio, setNovoInicio] = useState<number | null>(competenciaAtual());
  // "quem usa" da cadeia aberta (GET .../usos sob demanda)
  const [usosCadeia, setUsosCadeia] = useState<string | null>(null);
  const [usos, setUsos] = useState<{ total: number; usos: UsoTarifa[] } | null>(null);
  const [carregandoUsos, setCarregandoUsos] = useState(false);
  // TARIFAS SOLTAS (valores congelados no vínculo, fora do catálogo) — o monitor:
  // depois do pré-cadastro agrupado deveria esvaziar; reaparecer = alguém fixou
  // valor livre de novo. GET na abertura do modal (a contagem vai no botão) e a
  // cada expansão da seção.
  const [soltas, setSoltas] = useState<{ total: number; grupos: GrupoTarifaSolta[] } | null>(null);
  const [mostrarSoltas, setMostrarSoltas] = useState(false);
  // renomear a CADEIA inteira (PATCH { nome } — todas as vigências acompanham)
  const [renomeando, setRenomeando] = useState<string | null>(null);
  const [novoNome, setNovoNome] = useState("");
  // corrigir o VALOR de uma vigência (PATCH { valorCentavos, corrigirHistorico })
  // — o caminho do ERRO DE DIGITAÇÃO (in place); mudança temporal segue sendo o
  // "Novo valor a partir de…"
  const [editandoValorId, setEditandoValorId] = useState<string | null>(null);
  const [valorEdicao, setValorEdicao] = useState<number | null>(null);

  const textoErro = (e: unknown) => (e instanceof ErroApi ? String((e.corpo as { erro?: string })?.erro ?? e.message) : String(e));

  async function carregarSoltas() {
    try {
      const r = await api.get<{ total?: number; grupos?: GrupoTarifaSolta[] }>(`/beneficio-tipos/${tipo.id}/tarifas-soltas`);
      setSoltas({ total: r.total ?? 0, grupos: r.grupos ?? [] });
    } catch {
      // seção-monitor: falha não derruba o modal — o botão segue sem a contagem
      setSoltas(null);
    }
  }

  async function carregar() {
    try {
      const r = await api.get<{ vigentes?: TarifaCatalogo[]; historico?: TarifaCatalogo[] }>(`/beneficio-tipos/${tipo.id}/tarifas`);
      setDados({ vigentes: r.vigentes ?? [], historico: r.historico ?? [] });
    } catch (e) {
      setErro(textoErro(e));
    }
  }
  useEffect(() => {
    void carregar();
    void carregarSoltas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo.id]);

  const cadeias = dados ? montarCadeiasTarifas(dados.vigentes, dados.historico) : [];
  const nomesExistentes = cadeias.map((c) => c.nome);
  const nomeExiste = nomesExistentes.includes(nome.trim());

  async function postar(nomeTarifa: string, valorCentavos: number, vigenciaInicio: number, msg: string) {
    setErro(null);
    try {
      await api.post(`/beneficio-tipos/${tipo.id}/tarifas`, { nome: nomeTarifa, valorCentavos, vigenciaInicio });
      notificarOk(msg);
      await carregar();
      return true;
    } catch (e) {
      setErro(textoErro(e));
      return false;
    }
  }

  async function criar() {
    const ok = await postar(
      nome.trim(),
      valor!,
      inicio!,
      nomeExiste
        ? `Novo valor de "${nome.trim()}" a partir de ${fmtCompetencia(inicio!)} — a vigência anterior fica no histórico.`
        : `Tarifa "${nome.trim()}" cadastrada.`,
    );
    if (ok) {
      setNome("");
      setValor(null);
    }
  }

  async function criarNovoValor(nomeCadeia: string) {
    const ok = await postar(
      nomeCadeia,
      novoValor!,
      novoInicio!,
      `Novo valor de "${nomeCadeia}" a partir de ${fmtCompetencia(novoInicio!)} — os vínculos ligados à tarifa passam a usar o valor novo no mês certo.`,
    );
    if (ok) {
      setNovoValorCadeia(null);
      setNovoValor(null);
    }
  }

  async function alternarUsos(c: CadeiaTarifa) {
    if (usosCadeia === c.nome) {
      setUsosCadeia(null);
      setUsos(null);
      return;
    }
    setUsosCadeia(c.nome);
    setUsos(null);
    setCarregandoUsos(true);
    setErro(null);
    try {
      const alvo = c.aberta ?? c.vigencias[c.vigencias.length - 1]!;
      const r = await api.get<{ total: number; usos: UsoTarifa[] }>(`/beneficio-tipos/${tipo.id}/tarifas/${alvo.id}/usos`);
      setUsos(r);
    } catch (e) {
      setErro(textoErro(e));
      setUsosCadeia(null);
    } finally {
      setCarregandoUsos(false);
    }
  }

  async function alternarSoltas() {
    if (mostrarSoltas) {
      setMostrarSoltas(false);
      return;
    }
    setMostrarSoltas(true);
    await carregarSoltas(); // re-lê a cada expansão — é o monitor de valores novos congelados
  }

  /** O PATCH da cadeia mira a vigência aberta (fallback: a última) — o backend
   *  renomeia a cadeia INTEIRA (tipoId+nome). */
  async function renomear(c: CadeiaTarifa) {
    const novo = novoNome.trim();
    if (!novo || novo === c.nome) {
      setRenomeando(null);
      return;
    }
    setErro(null);
    try {
      const alvo = c.aberta ?? c.vigencias[c.vigencias.length - 1]!;
      await api.patch(`/beneficio-tipos/${tipo.id}/tarifas/${alvo.id}`, { nome: novo });
      notificarOk(`Tarifa renomeada para "${novo}" — a cadeia inteira (todas as vigências) acompanha.`);
      setRenomeando(null);
      await carregar();
    } catch (e) {
      setErro(textoErro(e));
    }
  }

  /** Correção de histórico do valor de UMA vigência (in place — aberta ou
   *  encerrada); NÃO é o caminho de mudança temporal (esse é "Novo valor a
   *  partir de…", que cria vigência nova). */
  async function corrigirValor(v: TarifaCatalogo) {
    setErro(null);
    try {
      await api.patch(`/beneficio-tipos/${tipo.id}/tarifas/${v.id}`, { valorCentavos: valorEdicao, corrigirHistorico: true });
      notificarOk(`Valor de "${v.nome}" corrigido no histórico — vale para o período desta vigência.`);
      setEditandoValorId(null);
      await carregar();
    } catch (e) {
      setErro(textoErro(e));
    }
  }

  async function encerrar() {
    if (!encerrando) return;
    try {
      await api.del(`/beneficio-tipos/${tipo.id}/tarifas/${encerrando.id}`);
      notificarOk(`Tarifa "${encerrando.nome}" encerrada — o histórico fica preservado.`);
      setEncerrando(null);
      await carregar();
    } catch (e) {
      setErro(textoErro(e));
      setEncerrando(null);
    }
  }

  return (
    <Modal opened onClose={onFechar} title={`Tarifas — ${tipo.nome}`} size="lg" centered>
      <Text size="xs" c="dimmed" mb="sm">
        Cada <strong>nome</strong> é uma cadeia temporal de valores: um <strong>valor novo para um nome existente</strong>{" "}
        é a vigência seguinte (a anterior fica no histórico) e os funcionários ligados à tarifa passam a usar o valor
        novo automaticamente, no mês certo. Dá para <strong>pré-cadastrar</strong> o valor futuro ("Novo valor a partir
        de…").
      </Text>
      <Button variant="subtle" size="compact-xs" mb="xs" onClick={() => void alternarSoltas()}>
        Tarifas soltas — valor fixado no funcionário{soltas ? ` (${soltas.total})` : ""}
      </Button>
      {mostrarSoltas && (
        <Card withBorder padding="sm" mb="sm">
          {!soltas ? (
            <Text size="xs" c="dimmed">
              carregando…
            </Text>
          ) : soltas.grupos.length === 0 ? (
            <Text size="xs" c="dimmed">
              nenhuma tarifa solta — todos estão no catálogo
            </Text>
          ) : (
            soltas.grupos.map((g) => (
              <Text key={g.vinculoId} size="xs" mt={2}>
                · {g.nome} (matr {g.matriculaDominio ?? "—"}) · {fmtCompetencia(g.vigenciaInicio)} →{" "}
                {g.vigenciaFim ? fmtCompetencia(g.vigenciaFim) : "—"} · itens:{" "}
                {g.itens.map((i) => `R$ ${fmtMoeda(i.tarifaCentavos)} ×${i.quantidade}`).join(", ")}
              </Text>
            ))
          )}
        </Card>
      )}
      {erro && (
        <Text size="sm" c="red.7" mb="sm" role="alert">
          {erro}
        </Text>
      )}
      {!dados ? (
        <Carregando linhas={3} />
      ) : (
        <>
          {cadeias.length === 0 && (
            <Text size="sm" c="dimmed" mb="sm">
              Nenhuma tarifa vigente no catálogo — os vínculos seguem com valor livre até a primeira tarifa cadastrada.
            </Text>
          )}
          {cadeias.map((c) => (
            <Card key={c.nome} withBorder padding="sm" mb="sm" data-testid={`cadeia-${c.nome}`}>
              <Group justify="space-between" wrap="nowrap" mb={2}>
                {renomeando === c.nome ? (
                  <Group gap={4} wrap="nowrap">
                    <TextInput
                      size="xs"
                      aria-label="Novo nome da tarifa"
                      value={novoNome}
                      onChange={(e) => setNovoNome(e.currentTarget.value)}
                      onKeyDownCapture={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          void renomear(c);
                        }
                        if (e.key === "Escape") {
                          e.stopPropagation();
                          setRenomeando(null);
                        }
                      }}
                      // o Esc daqui cancela o rename, NÃO fecha o modal (o listener
                      // de Escape do ModalBase é na janela, em capture — a saída
                      // oficial do Mantine é este atributo no target)
                      data-mantine-stop-propagation
                      autoFocus
                    />
                    <Button size="compact-xs" onClick={() => void renomear(c)} disabled={!novoNome.trim()}>
                      salvar
                    </Button>
                  </Group>
                ) : (
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={600}>
                      {c.nome}
                    </Text>
                    <Tooltip label="Renomear a tarifa (a cadeia inteira, todas as vigências)">
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={`Renomear tarifa ${c.nome}`}
                        onClick={() => {
                          setRenomeando(c.nome);
                          setNovoNome(c.nome);
                        }}
                      >
                        <IconPencil size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                )}
                <Group gap={4} wrap="nowrap">
                  <Button
                    variant="subtle"
                    size="compact-xs"
                    onClick={() => {
                      setNovoValorCadeia((atual) => (atual === c.nome ? null : c.nome));
                      setNovoValor(null);
                      setNovoInicio(competenciaAtual());
                    }}
                  >
                    Novo valor a partir de…
                  </Button>
                  <Button variant="subtle" size="compact-xs" onClick={() => void alternarUsos(c)}>
                    quem usa{usosCadeia === c.nome && usos ? ` (${usos.total})` : ""}
                  </Button>
                  {c.aberta && (
                    <Button variant="subtle" color="yellow.8" size="compact-xs" onClick={() => setEncerrando(c.aberta)}>
                      encerrar
                    </Button>
                  )}
                </Group>
              </Group>
              {c.vigencias.map((v) => (
                <div key={v.id}>
                  <Group gap={4} wrap="nowrap">
                    {v.vigenciaFim != null ? (
                      <Text size="xs" c="dimmed">
                        R$ {fmtMoeda(v.valorCentavos)} · {fmtCompetencia(v.vigenciaInicio)}–{fmtCompetencia(v.vigenciaFim)}
                      </Text>
                    ) : v.vigenciaInicio > competenciaAtual() ? (
                      <Text size="xs" fw={600}>
                        pré-cadastrado: R$ {fmtMoeda(v.valorCentavos)} a partir de {fmtCompetencia(v.vigenciaInicio)}
                      </Text>
                    ) : (
                      <Text size="xs" fw={600}>
                        R$ {fmtMoeda(v.valorCentavos)} · desde {fmtCompetencia(v.vigenciaInicio)}
                      </Text>
                    )}
                    <Tooltip label="Corrigir o valor desta vigência (erro de digitação — correção de histórico, in place)">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="xs"
                        aria-label="Editar valor da vigência"
                        onClick={() => {
                          setEditandoValorId(v.id);
                          setValorEdicao(v.valorCentavos);
                        }}
                      >
                        <IconPencil size={12} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                  {editandoValorId === v.id && (
                    <Group grow align="flex-end" mt={2} mb={4}>
                      <MoedaField
                        label="Valor corrigido"
                        description="correção de histórico — vale para o período desta vigência"
                        valor={valorEdicao}
                        onChange={setValorEdicao}
                      />
                      <Button size="xs" onClick={() => void corrigirValor(v)} disabled={valorEdicao === null}>
                        salvar correção
                      </Button>
                      <Button size="xs" variant="subtle" onClick={() => setEditandoValorId(null)}>
                        cancelar
                      </Button>
                    </Group>
                  )}
                </div>
              ))}
              {novoValorCadeia === c.nome && (
                <Group grow align="flex-end" mt="xs">
                  <MoedaField label="Novo valor" valor={novoValor} onChange={setNovoValor} />
                  <CompetenciaField label="A partir de" valor={novoInicio} onChange={setNovoInicio} anoMax={2030} />
                  <Button size="xs" onClick={() => void criarNovoValor(c.nome)} disabled={novoValor === null || novoInicio === null}>
                    Cadastrar novo valor
                  </Button>
                </Group>
              )}
              {usosCadeia === c.nome && (
                <div>
                  {carregandoUsos ? (
                    <Text size="xs" c="dimmed" mt={4}>
                      carregando…
                    </Text>
                  ) : !usos || usos.usos.length === 0 ? (
                    <Text size="xs" c="dimmed" mt={4}>
                      ninguém usa esta tarifa
                    </Text>
                  ) : (
                    usos.usos.map((u) => (
                      <Text key={u.vinculoId} size="xs" mt={2}>
                        · {u.nome} (matr {u.matriculaDominio ?? "—"}) · ×{u.quantidade} · {fmtCompetencia(u.vigenciaInicio)} →{" "}
                        {u.vigenciaFim ? fmtCompetencia(u.vigenciaFim) : "—"}
                      </Text>
                    ))
                  )}
                </div>
              )}
            </Card>
          ))}
          <Card withBorder padding="sm" mt="md">
            <Text size="sm" fw={600} mb={4}>
              Nova tarifa (nome novo) / novo valor de uma existente
            </Text>
            <Autocomplete
              label="Nome da tarifa"
              description="Nome NOVO = começa uma cadeia nova; nome já existente = novo valor dessa cadeia a partir da competência (nova vigência — a anterior fica no histórico)."
              data={nomesExistentes}
              value={nome}
              onChange={setNome}
              mb="sm"
            />
            <Group grow align="flex-end">
              <MoedaField label="Valor" valor={valor} onChange={setValor} />
              <CompetenciaField label="A partir de" valor={inicio} onChange={setInicio} anoMax={2030} />
            </Group>
            <Group justify="flex-end" mt="sm">
              <Button size="xs" onClick={() => void criar()} disabled={!nome.trim() || valor === null || inicio === null}>
                Cadastrar tarifa
              </Button>
            </Group>
          </Card>
        </>
      )}
      <Confirmacao
        aberta={encerrando !== null}
        titulo={`Encerrar a tarifa "${encerrando?.nome}"?`}
        consequencia="A tarifa deixa de valer para novos vínculos, mas NADA é apagado: ela fica no histórico e os vínculos que já a usam continuam com ela."
        rotuloConfirmar="Encerrar tarifa"
        onConfirmar={() => void encerrar()}
        onCancelar={() => setEncerrando(null)}
      />
    </Modal>
  );
}

export function BeneficiosPage() {
  const [tipos, setTipos] = useState<Tipo[] | null>(null);
  const [eventos, setEventos] = useState<EventoOpcao[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [editando, setEditando] = useState<Tipo | null>(null);
  const [criando, setCriando] = useState(false);
  const [desativando, setDesativando] = useState<Tipo | null>(null);
  const [historico, setHistorico] = useState<Tipo | null>(null);
  // tarifas do tipo VT (23/09/2026 — catálogo temporal de tarifas)
  const [tarifasTipo, setTarifasTipo] = useState<Tipo | null>(null);
  // avisos P2 do último tipo editado com propagação (meses fechados cobertos)
  const [avisosTipo, setAvisosTipo] = useState<string[]>([]);
  // DATA-BASE do catálogo (19/08/2026): null = atual; com valor, a lista mostra
  // os valores padrão das VERSÕES vigentes nela (inclusive tipos hoje desativados)
  const [dataBase, setDataBase] = useState<number | null>(null);
  const [avisosDataBase, setAvisosDataBase] = useState<string[]>([]);

  async function carregar() {
    try {
      const [t, e] = await Promise.all([
        api.get<{ tipos: (Tipo & { versao?: VersaoTipoUi; desativadoAtualmente?: boolean })[]; avisos?: string[] }>(
          dataBase === null ? "/beneficio-tipos" : `/beneficio-tipos?dataBase=${dataBase}`,
        ),
        api.get<{ eventos: EventoOpcao[] }>("/eventos"),
      ]);
      setTipos(t.tipos);
      setAvisosDataBase(t.avisos ?? []);
      setEventos(e.eventos.filter((x) => x.codigoDominio !== null));
    } catch (err) {
      setErro(err);
    }
  }
  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataBase]);

  async function desativar() {
    if (!desativando) return;
    try {
      await api.post(`/beneficio-tipos/${desativando.id}/desativar`);
      notificarOk(`"${desativando.nome}" desativado — os vínculos ficam no histórico.`);
      setDesativando(null);
      await carregar();
    } catch (e) {
      notificarErroApi(e);
    }
  }

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Benefícios (catálogo geral)</Title>
        <Group gap="md" wrap="nowrap">
          <CompetenciaField label="Ver o catálogo em" valor={dataBase ?? competenciaAtual()} onChange={setDataBase} />
          {dataBase !== null && (
            <Button variant="subtle" size="xs" onClick={() => setDataBase(null)}>
              voltar ao atual
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={() => setCriando(true)}>
            Novo tipo de benefício
          </Button>
        </Group>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Os tipos de benefício da empresa. Quem tem qual um (e desde/até quando) fica no vínculo de cada funcionário —
        benefícios não são iguais para todos. <strong>"Ver o catálogo em"</strong> mostra os valores padrão de outra
        data-base (inclusive benefícios desativados desde então).
      </Text>
      <ErroApiAlert erro={erro} />
      <AvisosAlert avisos={avisosTipo} />
      <AvisosAlert avisos={avisosDataBase} />
      {!tipos ? (
        <Carregando linhas={5} />
      ) : tipos.length === 0 ? (
        <Vazio texto="Nenhum tipo de benefício cadastrado — crie o primeiro." acao="Novo tipo" onAcao={() => setCriando(true)} />
      ) : (
        tipos.map((t) => {
          // em modo data-base, o papel pode vir da VERSÃO da época
          const exib = dataBase !== null && t.versao ? t.versao : t;
          return (
            <Card
              key={t.id}
              withBorder
              padding="sm"
              radius="md"
              mb={6}
              style={{ opacity: dataBase !== null && t.desativadoAtualmente === true ? 0.55 : 1 }}
            >
              <Group justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Group gap="xs" wrap="nowrap">
                    <Text fw={600} size="sm">
                      {t.nome}
                    </Text>
                    {dataBase !== null && t.desativadoAtualmente === true && (
                      <Badge variant="filled" color="gray" title="Este benefício foi desativado depois desta data-base — hoje não está mais no catálogo ativo">
                        desativado atualmente
                      </Badge>
                    )}
                  </Group>
                  {t.descricao && (
                    <Text size="xs" c="dimmed">
                      {t.descricao}
                    </Text>
                  )}
                </div>
                <Group gap={4} wrap="nowrap">
                  <Tooltip label="Editar o tipo">
                    <Button variant="light" size="compact-sm" aria-label={`Editar o tipo ${t.nome}`} onClick={() => setEditando(t)}>
                      Editar
                    </Button>
                  </Tooltip>
                  <Tooltip label="Linha do tempo das modificações e versões por data-base">
                    <Button variant="subtle" size="compact-sm" onClick={() => setHistorico(t)}>
                      Histórico
                    </Button>
                  </Tooltip>
                  {(exib.papel ?? t.papel) === "vt" && (
                    <Tooltip label="Catálogo temporal de tarifas de VT">
                      <Button variant="subtle" size="compact-sm" onClick={() => setTarifasTipo(t)}>
                        Tarifas
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip label="Some das telas de seleção de novos vínculos (os existentes seguem valendo)">
                    <Button variant="subtle" color="yellow.8" size="compact-sm" disabled={t.ativo === false} onClick={() => setDesativando(t)}>
                      Desativar
                    </Button>
                  </Tooltip>
                </Group>
              </Group>
            </Card>
          );
        })
      )}

      {(editando || criando) && (
        <ModalTipo
          tipo={criando ? null : editando}
          eventos={eventos}
          onFechar={() => {
            setEditando(null);
            setCriando(false);
          }}
          onSalvo={(avisos) => {
            setAvisosTipo(avisos);
            void carregar();
          }}
        />
      )}
      {historico && (
        <ModalHistoricoTipo
          tipo={historico}
          onFechar={() => setHistorico(null)}
          onAbrirConfig={() => {
            setEditando(historico);
            setHistorico(null);
          }}
        />
      )}
      {tarifasTipo && <ModalTarifasTipo tipo={tarifasTipo} onFechar={() => setTarifasTipo(null)} />}
      <Confirmacao
        aberta={desativando !== null}
        titulo={`Desativar "${desativando?.nome}"?`}
        consequencia="O tipo some das telas de seleção de novos vínculos. Os vínculos já existentes continuam valendo e ficam no histórico — nada é recalculado."
        rotuloConfirmar="Desativar"
        onConfirmar={() => void desativar()}
        onCancelar={() => setDesativando(null)}
      />
    </>
  );
}
